<template>
  <v-dialog :value="value" max-width="360" content-class="dialog-modern">
    <div class="dialog-container">
      <!-- Header -->
      <div class="dialog-header">
        <div class="header-icon">
          <v-icon size="24" color="#6200ea">mdi-lock-outline</v-icon>
        </div>
        <h2 class="dialog-title">{{ $t("recover.updateTitle") }}</h2>
        <p class="dialog-subtitle">{{ $t("recover.updateSubtitle") || 'Ingresa tu nueva contraseña' }}</p>
      </div>

      <!-- Form -->
      <div class="dialog-body">
        <v-form v-model="valid" ref="form">
          <div class="input-group">
            <label class="input-label">{{ $t('recover.newPassword') }}</label>
            <v-text-field
              v-model="password"
              type="password"
              validate-on-blur
              :rules="passwordRules"
              :placeholder="$t('recover.newPasswordPlaceholder') || 'Mínimo 8 caracteres'"
              outlined
              dense
              hide-details="auto"
              autocomplete="new-password"
              class="modern-input"
            ></v-text-field>
          </div>

          <div class="input-group">
            <label class="input-label">{{ $t('recover.repeatPassword') }}</label>
            <v-text-field
              v-model="repeat_password"
              type="password"
              validate-on-blur
              :rules="passwordRepeatRules"
              :placeholder="$t('recover.repeatPasswordPlaceholder') || 'Repite la contraseña'"
              outlined
              dense
              hide-details="auto"
              autocomplete="new-password"
              class="modern-input"
            ></v-text-field>
          </div>
        </v-form>
      </div>

      <!-- Actions -->
      <div class="dialog-actions">
        <button class="btn-cancel" @click="$emit('input', false)">
          {{ $t("global.dialog.btnCancel") }}
        </button>
        <button
          class="btn-submit"
          :class="{ 'btn-disabled': !valid || disabled }"
          :disabled="!valid || disabled"
          @click="submit"
        >
          {{ $t("global.dialog.btnUpdate") }}
        </button>
      </div>
    </div>

    <!-- Loading Dialog -->
    <v-dialog :value="loading" persistent width="200" content-class="loading-dialog">
      <div class="loading-container">
        <v-progress-circular
          indeterminate
          color="#6200ea"
          size="36"
          width="3"
        ></v-progress-circular>
        <span class="loading-text">{{ $t("home.waiting") }}...</span>
      </div>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapMutations, mapActions } from "vuex";

export default {
  props: {
    value: Boolean,
  },
  data() {
    return {
      valid: false,
      password: "",
      repeat_password: "",
      passwordRules: [
        (v) => !!v || this.$t("global.rules.passwordRequired"),
        (v) => v.length > 7 || this.$t("global.rules.passwordLength"),
      ],
      passwordRepeatRules: [
        (v) => !!v || this.$t("global.rules.passwordRequired"),
        (v) => v === this.password || this.$t("global.rules.passwordNoMatch"),
        (v) => v.length > 7 || this.$t("global.rules.passwordLength"),
      ],
      loading: false,
      disabled: false,
      form: {
        token: this.$route.query.token,
        password: "",
        passwordRepeat: "",
      },
      default: {
        token: this.$route.query.token,
        password: "",
        passwordRepeat: "",
      },
    };
  },
  methods: {
    ...mapMutations("ui", {
      openSnackbar: "openSnackbar",
    }),
    ...mapActions("ui", {
      showProgress: "showProgress",
      hideProgress: "hideProgress",
    }),
    submit() {
      this.form.password = this.password;
      this.form.passwordRepeat = this.repeat_password;
      if (this.$refs.form.validate()) {
        this.loading = true;
        this.$axios
          .post("/profile/update-passwd", this.form)
          .then(() => {
            this.loading = false;
            this.$emit("input", false);
            this.clear();
            this.$store.commit(
              "ui/openSnackbar",
              this.$t("recover.txtSuccessUpdate")
            );
          })
          .catch(() => {
            this.loading = false;
            this.openSnackbar(this.$t("recover.txtErrorUpdate"));
          });
      }
    },
    clear() {
      this.$refs.form.resetValidation();
      this.form = JSON.parse(JSON.stringify(this.default));
    },
  },
};
</script>

<style scoped>
.dialog-container {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
}

.dialog-header {
  padding: 24px 20px 16px;
  text-align: center;
}

.header-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #ede7f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px;
}

.dialog-subtitle {
  font-size: 13px;
  color: #888;
  margin: 0;
}

.dialog-body {
  padding: 0 20px 8px;
}

.input-group {
  margin-bottom: 16px;
}

.input-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.modern-input >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 44px !important;
}

.modern-input >>> .v-text-field__slot input {
  font-size: 14px;
}

.dialog-actions {
  display: flex;
  gap: 10px;
  padding: 12px 20px 20px;
}

.btn-cancel,
.btn-submit {
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:active {
  background: #eee;
  transform: scale(0.98);
}

.btn-submit {
  background: #6200ea;
  color: #fff;
}

.btn-submit:active {
  background: #5000d0;
  transform: scale(0.98);
}

.btn-disabled {
  background: #ccc !important;
  cursor: not-allowed;
}

.btn-disabled:active {
  transform: none;
}

.loading-container {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.loading-text {
  font-size: 14px;
  color: #666;
}
</style>

<style>
.dialog-modern {
  border-radius: 16px !important;
  overflow: hidden;
}

.loading-dialog {
  border-radius: 12px !important;
}
</style>
